import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
  Chip,
  Tooltip,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { useCardStore } from "../@shared/store/useCardsStore";
import {
  ConnectComponentsProvider,
  ConnectBalances,
  ConnectPayoutsList,
} from "@stripe/react-connect-js";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { collection, getDocs, doc } from "firebase/firestore";
import axios from "axios";
import CharacterCard from "../components/CharacterCard";
import NeoButton from "../components/NeoButton";
import { ArrowBackIos } from "@mui/icons-material";
import { MainContext } from "../App";
import CustomTabBar from "../components/playerDashboard/CustomTabBar";
import { db } from "../@shared/firebase/config";

const PlayerDashboard = () => {
  const { Theme, navigate } = useContext(MainContext);
  const { userData } = useAuthStore();
  const { characterCards } = useCardStore();

  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [demoMode, setDemoMode] = useState(true);
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [payoutsLoading, setPayoutsLoading] = useState(true);

  const [transactions, setTransactions] = useState([]);

  const [loadingTransactions, setLoadingTransactions] = useState(true);

  const handleTabChange = (e, newIndex) => setTabIndex(newIndex);

  const TABS = userData.collectionStripeId
    ? ["Payout History", "Transactions", "NFTs", "Collections"]
    : ["Payout History", "Transactions", "NFTs"];

  const isTest = window.location.origin.includes("localhost");

  useEffect(() => {
    if (stripeConnectInstance) {
      const timeout = setTimeout(() => {
        setBalancesLoading(false);
        setPayoutsLoading(false);
      }, 2800);
      return () => clearTimeout(timeout);
    }
  }, [stripeConnectInstance]);

  useEffect(() => {
    if (!userData?.stripeId && !userData?.stripeTestId) return;

    const fetchClientSecret = async () => {
      try {
        const res = await axios.post(
          "https://us-central1-infinft-card-game.cloudfunctions.net/app/account_session",
          {
            stripeId: isTest ? userData.stripeTestId : userData.stripeId,
            test: isTest,
          }
        );
        return res.data.client_secret;
      } catch (err) {
        console.error("❌ Failed to fetch client secret", err);
        return undefined;
      }
    };

    const initStripe = async () => {
      const instance = await loadConnectAndInitialize({
        publishableKey: isTest
          ? process.env.REACT_APP_STRIPE_TEST_KEY2
          : process.env.REACT_APP_STRIPE_LIVE_KEY,
        fetchClientSecret,
      });
      setStripeConnectInstance(instance);
    };

    initStripe();
  }, [userData?.stripeTestId]);

  useEffect(() => {
    if (demoMode) {
      setTransactions([
        {
          id: "demo_tx_1",
          customerUID: "demo_user_001",
          date: Date.now(),
          price: 4.99,
          productType: "Character card",
          product: {
            id: "demo_card_001",
            name: "Inferno Golem",
            description:
              "A fiery elemental beast that burns enemies with rage.",
            health: "12",
            attack: "5",
            defense: "3",
            accuracy: "2",
            speed: "4",
            rarity: "Epic",
            element: "Fire",
            imageUrl: "https://example.com/images/inferno-golem.png",
            ipProviderStripeId: "acct_demoIP",
            collectionStripeId: "acct_demoCOLL",
          },
        },
      ]);
      return;
    }

    const fetchTransactions = async () => {
      try {
        const salesRef = collection(
          doc(db, "users", "b8YaTVOKzNY0nL7rgD8vBRd1R9V2"),
          "sales"
        );
        const snapshot = await getDocs(salesRef);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTransactions(data);
      } catch (err) {
        console.error("❌ Error fetching transactions", err);
      } finally {
        setLoadingTransactions(false);
      }
    };

    if (userData?.id) fetchTransactions();
  }, [demoMode, userData?.id]);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const firestore = await import("firebase/firestore");
        const { collection, getDocs, doc } = firestore;
        const salesRef = collection(
          doc(firestore.db, "users", userData.id),
          "sales"
        );
        const snapshot = await getDocs(salesRef);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTransactions(data);
      } catch (err) {
        console.error("❌ Error fetching transactions", err);
      } finally {
        setLoadingTransactions(false);
      }
    };

    if (userData?.id) fetchTransactions();
  }, [userData?.id]);

  // NFT Ownership filter logic
  const ownedNFTs = userData?.roles.includes("ip_provider")
    ? characterCards.filter(
        (card) => card.ipProviderStripeId === userData.stripeId
      )
    : userData?.roles.includes("collection_creator")
    ? characterCards.filter(
        (card) => card.collectionStripeId === userData.stripeId
      )
    : [];

  const getAmountTotal = (prices) => {
    return prices.reduce((total, price) => {
      return (Number(total) + Number(price)).toFixed(2);
    }, 0);
  };

  return (
    <Grid item container xs={12} sx={{ p: 3, alignItems: "center" }}>
      <NeoButton
        isIcon={true}
        color={Theme.palette.tirciary.main}
        borderRadius={10}
        onClick={() => {
          navigate("/");
        }}
        icon={<ArrowBackIos style={{ color: "white" }} />}
      />
      <Typography variant="h5" sx={{ ml: 2 }}>
        Your Dashboard
      </Typography>

      {/* Top Stripe Balance Section */}
      <Grid item container xs={12} sx={{ minHeight: { xs: "30%", md: "20%" } }}>
        <Paper sx={{ p: 2, mb: 3, width: "100%" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Stripe Earnings
          </Typography>
          {stripeConnectInstance ? (
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {balancesLoading ? (
                  <CircularProgress />
                ) : (
                  <ConnectBalances
                    onReady={() => setBalancesLoading(false)} // Use if supported
                  />
                )}
              </Box>
            </ConnectComponentsProvider>
          ) : (
            <CircularProgress />
          )}
        </Paper>
      </Grid>

      <Grid
        item
        container
        xs={12}
        sx={{ minHeight: { xs: "calc(70% - 80px)", md: "calc(80% - 80px)" } }}
      >
        {/* Tabs for sections */}
        <Box
          style={{
            height: "fit-content",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomTabBar
            tabs={TABS}
            activeIndex={tabIndex}
            onChange={setTabIndex}
          />

          <Box sx={{ width: "100%" }}>
            {/* Payout History */}
            {tabIndex === 0 && stripeConnectInstance && (
              <ConnectComponentsProvider
                connectInstance={stripeConnectInstance}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Payout History
                  </Typography>
                  {balancesLoading ? (
                    <CircularProgress />
                  ) : (
                    <ConnectPayoutsList />
                  )}
                </Box>
              </ConnectComponentsProvider>
            )}

            {/* Transactions */}
            {tabIndex === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Your Transactions
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={demoMode}
                      onChange={(e) => setDemoMode(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Demo Mode"
                />

                {loadingTransactions ? (
                  <CircularProgress />
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Buyer</TableCell>
                          <TableCell>Item</TableCell>
                          <TableCell>
                            Amount{" "}
                            {transactions.length > 0
                              ? `$${getAmountTotal(
                                  transactions.map((t) => t.price)
                                )}`
                              : ""}
                          </TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.map((tx) => (
                          <TableRow key={tx.id}>
                            <TableCell>
                              {demoMode && (
                                <Chip
                                  label="Demo Data"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ mb: 1 }}
                                />
                              )}

                              {tx.customerUID || "N/A"}
                            </TableCell>
                            <TableCell>{tx.product.name || "N/A"}</TableCell>
                            <TableCell
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              ${tx.price?.toFixed(2) || "0.00"}{" "}
                              <Tooltip title="Price of total transaction. May not reflect your earnings from sale. See Stripe Dashboard.">
                                <HelpOutlineIcon
                                  sx={{ ml: 0.5, color: "grey" }}
                                />
                              </Tooltip>
                            </TableCell>

                            <TableCell>
                              {new Date(tx.date).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* NFTs */}
            {tabIndex === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Your NFTs
                </Typography>
                {ownedNFTs.length === 0 ? (
                  <Typography>No NFTs found for your account.</Typography>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    {ownedNFTs.map((card) => (
                      <Box
                        key={card.id}
                        sx={{ width: { xs: "50%", md: "30%", lg: "20%" } }}
                      >
                        <CharacterCard
                          {...card}
                          heightPer={80}
                          containerHeight={window.innerWidth >= 900 ? 460 : 300}
                          borderPx={5}
                          shineEffect={true}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Collection View (stub) */}
            {tabIndex === 3 &&
              userData.roles.includes("collection_creator") && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Your Collection
                  </Typography>

                  {ownedNFTs.length === 0 ? (
                    <Typography>
                      No NFTs found in your collection. Try minting or assigning
                      a card.
                    </Typography>
                  ) : (
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      {characterCards
                        .filter(
                          (card) =>
                            card.collectionStripeId === userData.stripeId
                        )
                        .map((card) => (
                          <Box
                            key={card.id}
                            sx={{ width: { xs: "100%", md: "30%", lg: "20%" } }}
                          >
                            <CharacterCard
                              {...card}
                              heightPer={80}
                              containerHeight={
                                window.innerWidth >= 900 ? 460 : 300
                              }
                              borderPx={5}
                              shineEffect={true}
                            />
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default PlayerDashboard;
